import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Box } from '../index';
import { useAuth } from '../../../context/AuthContext';
import { useFileUpload } from './hooks/useFileUpload';
import { usePricing } from './hooks/usePricing';
import MediaGrid from './components/MediaGrid';
import PricingSection from './components/PricingSection';
import MediaLibraryPicker from '../MediaLibraryPicker';
import { mediaService } from '../../../services/mediaService';
import { toast } from '../../../utils/notify';
import { FILE_UPLOAD } from '../../../constants/staticTexts';

const MediaUploader = ({ 
  onMediaUpdate, 
  onError, 
  maxFiles = 10, 
  allowedTypes = ['image/*', 'video/*', 'audio/*'],
  theme,
  droppedFiles,
  onFilesProcessed,
  triggerUpload,
  triggerLibrary,
  onTriggerReset,
  onTriggerLibraryReset,
  onShowUploader,
  isVisible = true,
  externalPricingTrigger,
  onReset,  // Changed from resetTrigger to onReset callback
  initialPrice = '', // New prop for external price sync
  postType = 'POST', // New prop to handle different post type rules
  context = 'post', // New prop: 'post' or 'message' to determine upload behavior
  initialMedia = [], // New prop for initial media when editing
  isEditing = false, // New prop to indicate edit mode
  onUploadingChange, // Callback to notify parent of uploading state
  onPriceError // Callback to notify parent of price validation errors
}) => {
  const { currentUser } = useAuth();

  // Helper to check accessSpec permissions
  const hasAccess = (feature) => {
    if (!currentUser?.accessSpec) {
      return currentUser?.creator ?? false;
    }
    return currentUser.accessSpec[feature] ?? false;
  };

  // Check if user can set pricing based on context
  const canSetPricing = context === 'message' 
    ? hasAccess('canReceivePaidMessages') 
    : hasAccess('canCreatePaidContent');
  // Helper to generate accept attribute for file input
  const getAcceptAttribute = () => {
    // Use file extensions instead of MIME type wildcards (e.g. video/*).
    // On iOS Safari, MIME wildcards like video/* trigger the Photos framework's
    // compatibility conversion (HEVC → H.264) before delivering the file to JS,
    // causing the "Preparing..." spinner and long delays on large videos.
    // File extensions bypass this — iOS delivers the original file as-is.
    // Server-side MediaConvert handles transcoding.
    // Validation in useFileUpload.js still enforces allowed formats via MIME types.
    return '.jpg,.jpeg,.gif,.png,.heic,.heif,.webp,.avif,.bmp,.tiff,'
      + '.mp4,.mov,.m4v,.mpg,.mpeg,.wmv,.avi,.webm,.mkv,.flv,.3gp,.3g2,'
      + '.mp3,.wav,.ogg,.aac,.flac,.m4a';
  };

  const [mediaItems, setMediaItems] = useState(() => {
    // Initialize with existing media if provided (for editing mode)
    return initialMedia || [];
  });
  const [showPricingSection, setShowPricingSection] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const initialMediaLoadedRef = useRef(false);
  const initialPriceSyncedRef = useRef(false);
  const fileInputRef = useRef(null);
  const wakeLockRef = useRef(null);
  const filePickerOpenRef = useRef(false);

  // Detect if running on iOS Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Request a screen wake lock to prevent the screen from going black
  // while iOS "Preparing..." transcodes a video in the background
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      }
    } catch {
      // Wake lock can fail if tab is hidden or battery saver is on — not critical
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    wakeLockRef.current?.release();
    wakeLockRef.current = null;
  }, []);

  // When iOS closes the file picker (timeout or cancel), it refocuses the page.
  // If onChange never fired, it means iOS failed to deliver the file.
  // We use focus + visibilitychange to detect this.
  useEffect(() => {
    if (!isIOS) return;

    const handleFocusBack = () => {
      if (!filePickerOpenRef.current) return;
      // Give onChange a moment to fire first (it fires before focus on success)
      setTimeout(() => {
        if (filePickerOpenRef.current) {
          // onChange never fired — picker closed without delivering a file
          filePickerOpenRef.current = false;
          releaseWakeLock();
          toast.error(
            "Video preparation timed out. Try a shorter clip, or go to Settings → Photos → 'Transfer to Mac or PC' → 'Keep Originals' to skip conversion.",
            { duration: 12000 }
          );
        }
      }, 1000);
    };

    window.addEventListener('focus', handleFocusBack);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') handleFocusBack();
    });

    return () => {
      window.removeEventListener('focus', handleFocusBack);
    };
  }, [isIOS, releaseWakeLock]);

  // Clean up wake lock on unmount
  useEffect(() => {
    return () => releaseWakeLock();
  }, [releaseWakeLock]);

  // Custom hooks - always use the same pricing logic
  const { 
    messagePrice, 
    setMessagePrice,
    handlePriceChange,
    updateParent,
    reset: resetPricing
  } = usePricing({ onUpdate: onMediaUpdate, postType });
  
  const { uploading, handleFiles } = useFileUpload({ 
    onError, 
    allowedTypes, 
    maxFiles,
    messagePrice,
    postType,
    context,
    uploadLimits: currentUser?.uploadLimits
  });

  // Notify parent of uploading state changes
  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  // Handle initial media when editing or restoring from draft
  // Only load once - don't re-add if user removes media
  useEffect(() => {
    if (!initialMediaLoadedRef.current && initialMedia && initialMedia.length > 0 && mediaItems.length === 0) {
      console.log('[MediaUploader] Setting initial media:', initialMedia);
      initialMediaLoadedRef.current = true; // Set ref BEFORE state update to prevent race condition
      setMediaItems(initialMedia);
    }
  }, [initialMedia, mediaItems.length]);

  // Handle files dropped from external source (text input area)
  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      handleFiles(droppedFiles, mediaItems, setMediaItems).then(() => {
        onFilesProcessed?.();
      });
    }
  }, [droppedFiles, handleFiles, mediaItems, onFilesProcessed]);

  // Handle trigger upload from parent (paperclip button)
  useEffect(() => {
    if (triggerUpload) {
      filePickerOpenRef.current = true;
      requestWakeLock();
      fileInputRef.current?.click();
      onTriggerReset?.();
    }
  }, [triggerUpload, onTriggerReset, requestWakeLock]);

  // Handle trigger library from parent (library button)
  useEffect(() => {
    if (triggerLibrary) {
      setShowLibraryPicker(true);
      onTriggerLibraryReset?.();
    }
  }, [triggerLibrary, onTriggerLibraryReset]);

  // Handle external pricing trigger (dollar sign button)
  useEffect(() => {
    if (externalPricingTrigger > 0) {
      // Don't show pricing section for post types that don't support pricing
      const pricingNotAllowed = postType === 'TARGET' || postType === 'POLL';
      
      if (!pricingNotAllowed) {
        setShowPricingSection(prev => !prev);
      }
    }
  }, [externalPricingTrigger, postType]);

  // Sync external price changes (from CreatePost price input) - SIMPLE VERSION
  useEffect(() => {
    // For posts, sync external price changes to internal MediaUploader price
    // For messages, apply once on mount when editing a scheduled message with an existing price
    // Only sync once on mount — prevents overwriting user edits when messagePrice changes
    if (initialPriceSyncedRef.current) return;

    if ((context === 'post' || context === 'message') && initialPrice !== undefined && initialPrice !== '' && initialPrice !== 0) {
      const priceStr = String(initialPrice);

      if (isEditing) {
        // When editing, media items already have correct isFreePreview flags from DB.
        // Set price directly without re-applying locking rules.
        setMessagePrice(priceStr);
        initialPriceSyncedRef.current = true;
        return;
      }

      // Non-edit context: apply handlePriceChange to sync price and media locking
      const updatedItems = handlePriceChange(priceStr, mediaItems);
      if (updatedItems && JSON.stringify(updatedItems) !== JSON.stringify(mediaItems)) {
        setMediaItems(updatedItems);
      }
      initialPriceSyncedRef.current = true;
    }
  }, [context, initialPrice, isEditing, setMessagePrice, handlePriceChange, mediaItems]);

  // Expose reset function via ref (called only when message is sent)
  const resetMediaUploader = useCallback(() => {
    setMediaItems(prevItems => {
      // Clean up any blob URLs before clearing
      prevItems.forEach(item => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
      return [];
    });
    setShowPricingSection(false);
    initialMediaLoadedRef.current = false; // Reset so draft can load again next time
    resetPricing();
  }, [resetPricing]);

  // Provide reset function to parent component
  useEffect(() => {
    if (onReset) {
      onReset(resetMediaUploader);
    }
  }, [onReset, resetMediaUploader]);

  // Update parent when media items or messagePrice changes
  useEffect(() => {
    updateParent(mediaItems, messagePrice);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItems, messagePrice]); // updateParent intentionally excluded to prevent infinite loop

  const handleFileSelect = (event) => {
    // onChange fired — picker delivered file(s) successfully
    filePickerOpenRef.current = false;
    releaseWakeLock();

    const files = Array.from(event.target.files || []);
    
    // iOS can close the picker without delivering a file (e.g. video too large
    // to transcode, memory pressure, or user waited too long). Give feedback.
    // Note: onChange only fires when a file was selected, NOT on cancel — so
    // an empty FileList here means iOS failed to prepare the file.
    if (files.length === 0) {
      toast.error(FILE_UPLOAD.VIDEO_SELECTION_FAILED, { duration: 8000 });
      return;
    }

    // Defer upload to next tick so iOS Safari's photo picker fully dismisses
    // before network requests start — prevents iOS from showing its own
    // upload progress overlay on top of the picker
    setTimeout(() => {
      handleFiles(files, mediaItems, setMediaItems).then((validFiles) => {
        if (validFiles && validFiles.length > 0) {
          onShowUploader?.();
        }
      });
    }, 0);

    // Reset the input so re-selecting the same file still triggers onChange
    event.target.value = '';
  };

  const removeMediaItem = async (id) => {
    setMediaItems(prev => {
      const updated = prev.filter(item => item.id !== id);
      const item = prev.find(item => item.id === id);
      
      // If the item has a mediaId (uploaded to server) and it's not from initial media (edit mode)
      // then delete it from the server immediately
      if (item?.mediaId && !initialMedia?.some(initialItem => initialItem.id === item.id)) {
        mediaService.deleteMedia(item.mediaId).catch(error => {
          console.error('Failed to delete media from server:', error);
          // Don't stop the removal from UI even if server deletion fails
        });
      }
      
      // Clean up preview URL
      if (item?.preview) {
        URL.revokeObjectURL(item.preview);
      }
      
      return updated;
    });
  };

  // New function to toggle item status between free and paid
  const toggleItemStatus = (id) => {
    setMediaItems(prev => {
      const currentPrice = parseFloat(messagePrice || 0);
      
      // If there's a price, we need to ensure at least one item stays locked
      if (currentPrice > 0) {
        const itemToToggle = prev.find(item => item.id === id);
        
        // If we're trying to unlock this item (make it free preview)
        if (itemToToggle && !itemToToggle.isFreePreview) {
          // Count how many items would be locked after this change
          const lockedItemsAfterToggle = prev.filter(item => 
            item.id === id ? false : !item.isFreePreview
          ).length;
          
          // Prevent unlocking if this would make all items free
          if (lockedItemsAfterToggle === 0) {
            toast.warning('At least one media item must remain locked when there\'s a price');
            return prev; // Don't change anything
          }
        }
      }
      
      // Safe to toggle
      return prev.map(item => 
        item.id === id ? { ...item, isFreePreview: !item.isFreePreview } : item
      );
    });
  };

  // Function to update specific properties of a media item (e.g., selected thumbnail)
  const updateMediaItem = (id, updates) => {
    setMediaItems(prev => 
      prev.map(item => 
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };

  // Function to trigger file input
  const handleAddMedia = () => {
    filePickerOpenRef.current = true;
    requestWakeLock();
    fileInputRef.current?.click();
  };

  // Handle library item selection from MediaLibraryPicker
  const handleLibrarySelect = useCallback((selectedItems) => {
    setShowLibraryPicker(false);
    if (!selectedItems || selectedItems.length === 0) return;

    const available = maxFiles - mediaItems.length;
    const toAdd = selectedItems.slice(0, available);

    if (toAdd.length < selectedItems.length) {
      toast.warning(`Only ${toAdd.length} item(s) added — max ${maxFiles} files allowed.`);
    }

    const currentPrice = parseFloat(messagePrice || 0);
    const newItems = toAdd.map(item => ({
      id: item.id,
      mediaFileId: item.id,
      isLibraryItem: true,
      uploadedToS3: true,
      processed: true,
      s3Key: item.s3Key,
      fileName: item.fileName,
      mimeType: item.mimeType,
      mediaType: item.mediaType,
      thumbnailUrl: item.thumbnailUrl,
      width: item.width,
      height: item.height,
      duration: item.duration,
      isFreePreview: currentPrice <= 0, // free when no price set; locked when price > 0
      uploading: false,
    }));

    setMediaItems(prev => [...prev, ...newItems]);
    onShowUploader?.();
  }, [mediaItems.length, maxFiles, messagePrice, onShowUploader]);

  const handlePriceChangeWrapper = (value) => {
    const updatedItems = handlePriceChange(value, mediaItems);
    if (updatedItems !== mediaItems) {
      setMediaItems(updatedItems);
    }
  };

  return (
    <>
      {/* Hidden input for file upload - always present */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={getAcceptAttribute()}
        multiple
        style={{ display: 'none' }}
      />

      {/* Upload Library picker modal */}
      {showLibraryPicker && (
        <MediaLibraryPicker
          maxSelections={maxFiles - mediaItems.length}
          onSelect={handleLibrarySelect}
          onClose={() => setShowLibraryPicker(false)}
        />
      )}

      {/* Visible UI - Show when component is visible AND (has media items OR pricing section is active) */}
      {isVisible && (mediaItems.length > 0 || showPricingSection) && (
        <UploaderContainer>
          <MediaOrganizer>
            <MediaGrid
              mediaItems={mediaItems}
              messagePrice={messagePrice}
              uploading={uploading}
              onRemoveItem={removeMediaItem}
              onToggleItemStatus={toggleItemStatus}
              onUpdateItem={updateMediaItem}
              onAddMedia={handleAddMedia}
              maxFiles={maxFiles}
              isEditing={isEditing}
            />
            {/* Only show pricing section if user has permission */}
            {showPricingSection && postType !== 'TARGET' && postType !== 'POLL' && canSetPricing && (
              <PricingSection
                messagePrice={messagePrice}
                onPriceChange={handlePriceChangeWrapper}
                pricingLimits={currentUser?.pricingLimits}
                onPriceError={onPriceError}
                context={context}
              />
            )}
          </MediaOrganizer>
        </UploaderContainer>
      )}
    </>
  );
};

// Styled Components using common components
const UploaderContainer = styled(Box)`
  width: 100%;
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
`;

const MediaOrganizer = styled(Box)`
  width: 100%;
`;

export default MediaUploader;
